import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, UserRound, FolderKanban, MessageSquare, History } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import { Panel, Loading, LoadError, StatusBadge, PriorityBadge, COMM_TYPE_LABELS, fmtDate, fmtDateTime } from "../ui";

export const AdminPatientDetail = () => {
    const { patientId } = useParams();
    const { user } = useAdminAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api
            .get(`/admin/patients/${patientId}`)
            .then((res) => setData(res.data))
            .catch((err) =>
                setError(
                    err.response?.status === 403
                        ? "This patient's records are not assigned to you."
                        : "Could not load this patient. The record may not exist."
                )
            );
    }, [patientId, user]);

    if (error) return <LoadError message={error} />;
    if (!data) return <Loading />;

    const { patient, cases, communications, activities } = data;

    return (
        <div data-testid="admin-patient-detail">
            <Link
                to="/admin/patients"
                data-testid="patient-back-link"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] transition-colors"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> All patients
            </Link>

            <div className="mt-4">
                <h1 className="text-2xl font-bold tracking-tight text-[#F7F5EF]" data-testid="patient-detail-title">
                    {patient.name}
                </h1>
                <p className="mt-1 text-sm text-[#64716D]">
                    {cases.length} case{cases.length === 1 ? "" : "s"} · first seen {fmtDate(patient.created_at)}
                </p>
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Panel className="p-5">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <UserRound className="w-4 h-4" /> Patient information
                        </h2>
                        <dl className="mt-3 grid sm:grid-cols-2 gap-x-8">
                            {[
                                ["Email", patient.email],
                                ["Phone / WhatsApp", patient.phone],
                                ["Country", patient.country],
                                ["City", patient.city],
                                ["Date of birth", patient.date_of_birth],
                                ["Preferred language", patient.preferred_language],
                            ].map(([label, value]) => (
                                <div key={label} className="flex items-start justify-between gap-4 py-2 border-b border-[#1A2E2A]">
                                    <dt className="text-xs uppercase tracking-wider text-[#64716D]">{label}</dt>
                                    <dd className="text-sm text-[#F7F5EF] text-right break-words">{value || "—"}</dd>
                                </div>
                            ))}
                        </dl>
                    </Panel>

                    <Panel className="overflow-hidden">
                        <h2 className="flex items-center gap-2 px-5 pt-5 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <FolderKanban className="w-4 h-4" /> Cases
                        </h2>
                        {cases.length === 0 ? (
                            <p className="px-5 py-6 text-sm text-[#64716D]">No cases in your scope.</p>
                        ) : (
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full text-left" data-testid="patient-cases-table">
                                    <thead>
                                        <tr className="border-y border-[#1A2E2A] bg-[#0E1A17]">
                                            {["Case", "Status", "Priority", "Coordinator", "Created"].map((h) => (
                                                <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#64716D]">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cases.map((c) => (
                                            <tr key={c.case_id} data-testid={`patient-case-row-${c.case_id}`} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                                                <td className="px-4 py-2.5 text-xs font-mono">
                                                    <Link to={`/admin/cases/${c.case_id}`} data-testid={`patient-case-link-${c.case_id}`} className="text-emerald-300 hover:text-emerald-200">
                                                        {c.case_id}
                                                    </Link>
                                                </td>
                                                <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                                                <td className="px-4 py-2.5"><PriorityBadge priority={c.priority} /></td>
                                                <td className="px-4 py-2.5 text-sm text-[#A3B8B0] whitespace-nowrap">{c.assigned_coordinator?.name || "Unassigned"}</td>
                                                <td className="px-4 py-2.5 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(c.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Panel>
                </div>

                <div className="space-y-6">
                    <Panel className="p-5" data-testid="patient-communications">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <MessageSquare className="w-4 h-4" /> Communications
                        </h2>
                        {communications.length === 0 ? (
                            <p className="mt-3 text-sm text-[#64716D]">No communication entries yet.</p>
                        ) : (
                            <ul className="mt-4 space-y-3">
                                {communications.map((c, i) => (
                                    <li key={c.id || i} className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2.5">
                                        <p className="text-xs font-semibold text-emerald-300">
                                            {COMM_TYPE_LABELS[c.type] || c.type} · {c.case_id}
                                        </p>
                                        <p className="mt-1 text-sm text-[#F7F5EF]">{c.note}</p>
                                        <p className="mt-1 text-xs text-[#64716D]">{c.staff_name} · {fmtDateTime(c.created_at)}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Panel>

                    <Panel className="p-5" data-testid="patient-activity">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <History className="w-4 h-4" /> Activity
                        </h2>
                        {activities.length === 0 ? (
                            <p className="mt-3 text-sm text-[#64716D]">No activity recorded yet.</p>
                        ) : (
                            <ul className="mt-4 space-y-3">
                                {activities.slice(0, 25).map((a, i) => (
                                    <li key={a.id || i} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500/80" />
                                        <div>
                                            <p className="text-sm text-[#F7F5EF]">{a.event}</p>
                                            <p className="text-xs text-[#64716D]">
                                                {a.case_id} · {a.user_name} · {fmtDateTime(a.created_at)}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Panel>
                </div>
            </div>
        </div>
    );
};
